import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { GetBillTosApiParameter, GetShipTosApiParameter } from "@insite/client-framework/Services/CustomersService";
import selectShipTo from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SelectShipTo";
import setAsDefault from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetAsDefault";
import translate from "@insite/client-framework/Translate";
import { BaseAddressModel, CustomerValidationDto } from "@insite/client-framework/Types/ApiModels";
import AddressInfoDisplay, { AddressInfoDisplayStyles } from "@insite/content-library/Components/AddressInfoDisplay";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon from "@insite/mobius/Icon";
import EditIcon from "@MasterBluePrint/Icons/EditIcon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Radio, { RadioComponentProps } from "@insite/mobius/Radio";
import RadioGroup, { RadioGroupProps } from "@insite/mobius/RadioGroup";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { useState } from "react";
import styled, { css } from "styled-components";

type BaseAddressModelWithValidation = BaseAddressModel & { validation?: CustomerValidationDto | null };

interface OwnProps {
    customer: BaseAddressModelWithValidation;
    isSelected?: boolean;
    onSelect: (customer: BaseAddressModel) => void;
    /** @deprecated Not used anymore */
    allowEditCustomer?: boolean;
    onEdit?: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>, customer: BaseAddressModel) => void;
    extendedStyles?: CustomerCardStyles;
    shipTosParameter: GetShipTosApiParameter;
}

export interface CustomerCardStyles {
    container?: GridContainerProps;
    addressGridItem?: GridItemProps;
    address?: AddressInfoDisplayStyles;
    actionsGridItem?: GridItemProps;
    actionsWrapper?: InjectableCss;
    selectButton?: ButtonPresentationProps;
    editLink?: LinkPresentationProps;
    checkoutAddressWrapper?:InjectableCss;
}

export const customerCardStyles: CustomerCardStyles = {
    container: {
        css: css`
            padding: 1rem 0;
            border-bottom: 1px solid #C2D1D9;

            > div {
                flex-direction: column;
            }

            address {
                color: #2E384D;
                font-family: Lato;
                font-size: 16px;
                letter-spacing: 0;
                line-height: 24px;

                p, span {
                    color: #2E384D;
                }
            }

            label {
                color: #2E384D;
                font-family: Lato;
                font-size: 15px;
                letter-spacing: 0.17px;
                line-height: 18px;
            }
        `,
    },
    addressGridItem: { 
        width: 12,
        css: css`
            padding-top: 5px;
        `,
     },
    actionsGridItem: {
        width: 12,
        css: css`
            justify-content: space-between;
            padding-bottom: 0;
            padding-left: 10px;
        `,
    },
    actionsWrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            align-items: center;
            height: 100%;
            width: 100%;
            justify-content: space-between;
            align-items: flex-start;
        `,
    },
    selectButton: { 
        variant: "tertiary",
        css: css`
            color: #2E384D !important;
            font-family: Lato;
            font-size: 15px;
            letter-spacing: 0.17px;
            line-height: 18px;
            height: auto;
            position: relative;

            padding: 0 !important;
            border: 0 !important;
            margin: 0 !important;
            background: none !important;

            > span {
                font-weight: normal;            
            }

            &:before {
                content: "";
                box-sizing: border-box;
                display: inline-block;
                height: 20px;
                width: 20px;
                border-radius: 50%;
                border: 1px solid #002554;
                margin-right: 10px;
                position: relative;
                top: 4px;
            }

            &:after {
                content: "";
                display: block;
                height: 10px;
                width: 10px;
                border-radius: 50%;
                background: #fff;
                position: absolute;
                left: 5px;
                top: calc(50% - 3px);
            }

            &.active {
                color: #2E384D !important;

                > span {
                    font-weight: bold;
                }

                &:before {
                    border-color: #002554;
                    background: #fff;
                }

                &:after {
                    background: #002554;
                }
            }

            &:hover, &:focus, &:active {
                padding: 0 !important;
                border: 0 !important;
                margin: 0 !important;
                background: none !important;
            }
        `,
     },
    editLink: {
        css: css`
            margin-top: 0;
            margin-left: 20px;
            padding-left: 15px;
            margin-right: 0;
            border-left: 1px solid #E4E8F0;

            svg {
                width: 20px;
                height: 20px;
            }
        `,
    },
    checkoutAddressWrapper:{
        css: css`
        label {

            color: red;
            
        }
    `,
    },
};

const ActionsWrapper = styled.div<InjectableCss>`
    ${({ css }) => css}
`;

const CustomerCard = ({ customer, isSelected, onEdit, extendedStyles, onSelect }: OwnProps) => {
    const [styles] = useState(() => mergeToNew(customerCardStyles, extendedStyles));

    const handleEditClick = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        onEdit?.(event, customer);
    };

    const hasEditableFields = true;
    return (
        <GridContainer {...styles.container}>
            <GridItem {...styles.actionsGridItem}>
                <ActionsWrapper {...styles.actionsWrapper}>
                    <Button
                        {...styles.selectButton}
                        onClick={() => onSelect(customer)}
                        disabled={isSelected}
                        data-test-selector={`customerCard_select_${customer.id}`}
                        className={`customer-card-address ${isSelected ? "active" : ""}`}
                    >
                        {isSelected ? "Selected Address" : "Select Address"}
                    </Button>
                    {/* <RadioGroup disabled={isSelected} onChangeHandler={() => onSelect(customer)} label="" value="">
                        <Radio >{isSelected ? "Selected Address" : "Select Address"}</Radio>
                    </RadioGroup> */}
                    {hasEditableFields && onEdit && (
                        <Link
                            {...styles.editLink}
                            onClick={handleEditClick}
                            data-test-selector={`customerCard_edit_${customer.id}`}
                        >
                        <Icon src={EditIcon} />
                        <VisuallyHidden>{translate("Edit")}</VisuallyHidden>
                        </Link>
                    )}
                </ActionsWrapper>
            </GridItem>

            <GridItem {...styles.addressGridItem}>
                <StyledWrapper {...styles.checkoutAddressWrapper}>
                {!(hasEditableFields) ? (                  
                    <label>
                        {translate("Can modify checkout address message")}
                    </label>
                    
                  ):""}
                <AddressInfoDisplay
                
                    {...customer}
                    state={customer.state?.abbreviation}
                    country={customer.country?.abbreviation}
                    extendedStyles={styles.address}
                />
                </StyledWrapper>
                
               
            </GridItem>
        </GridContainer>
    );
};

export default CustomerCard;
